'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';
// no type def available
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

try {
    pdfjsLib.GlobalWorkerOptions.workerPort = new Worker( new URL( 'pdfjs-dist/build/pdf.worker.js', import.meta.url ) );
}
catch ( e ) {
    /*empty*/
}

import {Column} from 'primereact/column';
import {
    DataTable,
    DataTableRowClickEvent,
    DataTableSortEvent,
    DataTableSelectionMultipleChangeEvent,
    DataTableRowData,
    DataTableExpandedRows,
    DataTableRowToggleEvent
} from 'primereact/datatable';
import {
    FileUpload,
    FileUploadHeaderTemplateOptions,
    FileUploadRemoveEvent,
    FileUploadSelectEvent,
    FileUploadUploadEvent,
    ItemTemplateOptions
} from 'primereact/fileupload';
import StaticContentField, {
    ComponentProps as StaticContentFieldComponentProps,
    componentProps as staticContentFieldComponentProps
} from '~/frontend/src/components/base/shared/field/StaticContentField';
import {Toolbar} from 'primereact/toolbar';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import Item, {
    ItemAttributes
} from '~/shared/src/ci/Item';
import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {ReferenceValue} from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {FileUploadResult} from '~/shared/src/utilities/modules/file';
import {
    FileFieldListingOrder,
    FileFieldPresentation,
    FileFieldPreview
} from '~/shared/src/utilities/modules/form';
import {
    formatBytes,
    formatTimestamp
} from '~/shared/src/utilities/valueFormaters';
import messageUtils from '~/shared/src/utilities/messageUtils';

import FieldWrapper, {
    defaultProps as fieldWrapperDefaultProps,
    DefaultProps as FieldWrapperDefaultProps,
    componentProps as fieldWrapperComponentProps,
    ComponentProps as FieldWrapperComponentProps,
    MessageResolver,
    Props as FieldWrapperProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {statusColoringRenderer} from '~/frontend/src/utilities/valueRenderers';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';

type FieldElusiveData = {
    gridExpandedRows? : any[] | DataTableExpandedRows;
    gridSelection? : TransformedValue[];
    gridSortOrder? : FileFieldListingOrder;
};

const setElusiveData = origSetElusiveData as ( panelKey : string, data : FieldElusiveData ) => SetElusiveDataAction;


type PropertyTypeIds = {
    contentType? : number;
    extension? : number;
    fileID? : number;
    originalName? : number;
    size? : number;
    uploadDate? : number;
};
type PropertySetIds = {
    contentType? : number;
    extension? : number;
    fileID? : number;
    originalName? : number;
    size? : number;
    uploadDate? : number;
};
type TransformedValue = {
    id : number;
    statusId : number;
    name : string;
    size : number;
    uploadDate : Date | undefined;
    sortLabel : string | number | Date;
    itemId : number;
    item? : Item;
};
const transformedValueKeys = keys<TransformedValue>();

type RowData = TransformedValue & {
    deleteBody? : React.ReactNode;
    nameBody? : React.ReactNode;
    sizeBody? : React.ReactNode;
    uploadDateBody? : React.ReactNode;
    preview? : React.ReactNode;
};
type CacheParameters = {
    id : string;
    statusId : number;
    referenceId? : number;
    itemId? : number;
    disabled? : boolean;
    preview? : string;
    dirty? : boolean;
    value? : AttributeValue;
    language? : string;
};
type ColumnCache = {
    cacheParameters : CacheParameters;
    column : React.ReactNode;
}
type CellCache = {
    [ cellId : string ] : {
        cacheParameters : CacheParameters;
        body : React.ReactNode;
        preview? : React.ReactNode;
    }
};
type  ColumnId = 'delete' | 'expand' | 'name' | 'size' | 'uploadDate';
type ColumnsCache = {
    [columnId in ColumnId]? : {
        columnCache : ColumnCache;
        cellCache : CellCache;
    };
};

type DefaultProps = FieldWrapperDefaultProps & {
    schema? : typeof defaultBaseRequestParameters[ 'schema' ];

    /**
     * The maximum allowed file size in bytes.
     * Set to 0 for unlimited size.
     */
    maxFileSize? : number;

    /**
     * The number of text rows to be displayed within the field.
     * Set to 0 to fit all content or reach maxHeight.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * Set to 0 for unlimited height.
     */
    maxHeight? : number;

    /**
     * How to sort the keys and/or values
     */
    listingOrder? : FileFieldListingOrder;

    /**
     * How to present the field.
     */
    presentation? : FileFieldPresentation;

    preview? : FileFieldPreview;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The placeholder text to show on empty fields for new entries.
     */
    placeholder? : string;

    /**
     * Is this field is disabled.
     */
    disabled? : boolean;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;
};

const defaultProps : Required<DefaultProps> = {
    schema: defaultBaseRequestParameters[ 'schema' ],

    ...fieldWrapperDefaultProps,

    maxFileSize    : 0,
    height         : 3,
    maxHeight      : 0,
    listingOrder   : 'fileNameAsc',
    presentation   : 'grid',
    preview        : 'expanded',
    markDirty      : false,
    placeholder    : 'YN.base.form.fileField.placeholder',
    disabled       : false,
    inputClassNames: [],
    inputStyle     : {}
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    /**
     * The value.
     */
    value? : ReferenceValue | ReferenceValue[];

    /**
     * The value to determine dirty against.
     */
    originalValue? : ReferenceValue | ReferenceValue[];

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The item for which the reference(s) is for.
     */
    rootItemId : number;

    /**
     * The ReferenceSet ID for the reference(s) of this field.
     */
    referenceSetId : number;

    /**
     * The statusId for new entries.
     */
    newStatusId? : number;

    /**
     * The statusId for deleting entries.
     */
    deleteStatusId? : number;

    /**
     * A comma-separated list of one or more file types, or unique file type specifiers, describing which file types to allow.
     *
     * example 1: image/*,.pdf
     * example 2: .doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document
     */
    acceptedFiles? : string;

    /**
     * The placeholder text to show on empty fields for new entries.
     */
    placeholder? : string;

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    onChange : ( value : ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Omit<FieldWrapperComponentProps, 'children' | 'displayValue' | 'pt'> & Required<DefaultProps> & Props;


const FileField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const dispatch : AppDispatch                                  = useDispatch(),
              user                                                    = useSelector( ( state : StoreState ) => state.session?.user ),
              {
                  uid,
                  acceptedFiles,
                  dataContext,
                  deleteStatusId,
                  disabled,
                  errorMessage,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  listingOrder,
                  markDirty,
                  maxFileSize,
                  maxHeight,
                  messagePlaceholders,
                  newStatusId,
                  onChange,
                  originalValue,
                  placeholder,
                  presentation,
                  preview,
                  pt,
                  referenceSetId,
                  rootItemId,
                  schema,
                  value
              }                                                       = props,
              elementId                                               = id ?? `${uid}_fileField`,
              elusiveData : FieldElusiveData | undefined              = useSelector( ( state : StoreState ) => state.elusiveData?.[ elementId ] as FieldElusiveData | undefined ),
              currentColumnsCache                                     = useRef<ColumnsCache>( {} ),
              fileUploadRef                                           = useRef<FileUpload>( null ),
              tableMinWidth                                           = useRef<number>( 0 ),
              [ loading, setLoading ]                                 = useState<boolean>( false ),
              gridExpandedRows                                        = useMemo(
                  () => {
                      return elusiveData?.gridExpandedRows;
                  },
                  [
                      elusiveData?.gridExpandedRows
                  ]
              ),
              gridSortOrder                                           = useMemo(
                  () => {
                      return elusiveData?.gridSortOrder ?? listingOrder;
                  },
                  [
                      elusiveData?.gridSortOrder
                  ]
              ),
              gridSelection                                           = useMemo(
                  () => {
                      return elusiveData?.gridSelection ?? [];
                  },
                  [
                      elusiveData?.gridSelection
                  ]
              ),
              referenceSet : ReferenceSet | undefined                 = useMemo(
                  () => metaModel[ schema ].getReferenceSet( referenceSetId ),
                  [
                      schema,
                      referenceSetId
                  ]
              ),
              currentReferenceSet                                     = useRef<ReferenceSet | undefined>( referenceSet ),
              deletedStatusIds                                        = useMemo(
                  () => metaModel[ schema ].getDeletedStatus().map( s => s.id ),
                  [
                      schema
                  ]
              ),
              fileItemTypeId                                          = useMemo(
                  () => {
                      return metaModel[ schema ].getItemType( 'File' )?.id;
                  },
                  [
                      schema
                  ]
              ),
              referenceDirection                                      = useMemo(
                  () => {
                      return referenceSet?.srcItemTypeId === fileItemTypeId ? 'DestToSrc' : 'SrcToDest';
                  },
                  [
                      referenceSet
                  ]
              ),
              propertyTypeIds : PropertyTypeIds                       = useMemo(
                  () => {
                      return {
                          contentType : metaModel[ schema ].getPropertyType( 'ContentType' )?.id,
                          extension   : metaModel[ schema ].getPropertyType( 'Extension' )?.id,
                          fileID      : metaModel[ schema ].getPropertyType( 'FileID' )?.id,
                          originalName: metaModel[ schema ].getPropertyType( 'OriginalName' )?.id,
                          size        : metaModel[ schema ].getPropertyType( 'Size' )?.id,
                          uploadDate  : metaModel[ schema ].getPropertyType( 'UploadDate' )?.id
                      }
                  },
                  [
                      schema
                  ]
              ),
              propertySetIds : PropertySetIds                         = useMemo(
                  () => {
                      return {
                          contentType : fileItemTypeId && propertyTypeIds?.contentType ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.contentType )?.id : undefined,
                          extension   : fileItemTypeId && propertyTypeIds?.extension ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.extension )?.id : undefined,
                          fileID      : fileItemTypeId && propertyTypeIds?.fileID ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.fileID )?.id : undefined,
                          originalName: fileItemTypeId && propertyTypeIds?.originalName ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.originalName )?.id : undefined,
                          size        : fileItemTypeId && propertyTypeIds?.size ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.size )?.id : undefined,
                          uploadDate  : fileItemTypeId && propertyTypeIds?.uploadDate ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( fileItemTypeId, propertyTypeIds?.uploadDate )?.id : undefined
                      }
                  },
                  [
                      fileItemTypeId,
                      propertyTypeIds,
                      schema
                  ]
              ),
              transformedValue                                        = useMemo(
                  () => {
                      const retVal : TransformedValue[] = [];

                      if ( value && referenceSet ) {
                          let entries : ReferenceValue[];

                          if ( !Array.isArray( value ) ) {
                              entries = [ value ];
                          }
                          else {
                              entries = value;
                          }

                          for ( const entry of entries ) {
                              if ( !deletedStatusIds.includes( entry.statusId ) ) {
                                  let item                  = entry.item,
                                      originalName : string = '',
                                      size : number         = 0,
                                      uploadDate : Date | undefined;

                                  if ( !item ) {
                                      const itemId = entry[ referenceDirection === 'SrcToDest' ? 'destItemId' : 'srcItemId' ];

                                      item = global.data[ dataContext ].items[ itemId ];
                                  }

                                  if ( item ) {
                                      originalName = propertyTypeIds?.originalName ? item.properties.get( propertyTypeIds.originalName )?.value as string ?? '' : '';
                                      size = propertyTypeIds?.size ? item.properties.get( propertyTypeIds.size )?.value as number : 0;
                                      uploadDate = propertyTypeIds?.uploadDate ? item.properties.get( propertyTypeIds.uploadDate )?.value as Date : undefined;

                                      if ( originalName && size && uploadDate ) {
                                          let sortLabel : string | number | Date | undefined = originalName.toLowerCase();

                                          if ( listingOrder.indexOf( 'fileSize' ) !== -1 ) {
                                              sortLabel = size;
                                          }
                                          else if ( listingOrder.indexOf( 'uploadDate' ) !== -1 ) {
                                              sortLabel = uploadDate;
                                          }

                                          retVal.push( {
                                              ..._.pick( entry, transformedValueKeys ) as TransformedValue,
                                              name     : originalName,
                                              size,
                                              uploadDate,
                                              sortLabel: sortLabel ?? '',
                                              itemId   : item.id,
                                              item     : entry.item
                                          } );
                                      }
                                  }
                              }
                          }
                      }

                      retVal.sort( ( value1, value2 ) => {
                          if ( value1.sortLabel > value2.sortLabel ) {
                              return 1;
                          }
                          else if ( value1.sortLabel < value2.sortLabel ) {
                              return -1;
                          }
                          else {
                              return 0;
                          }
                      } );

                      if ( listingOrder.indexOf( 'Desc' ) !== -1 ) {
                          retVal.reverse();
                      }

                      return retVal;
                  },
                  [
                      dataContext,
                      elementId,
                      listingOrder,
                      JSON.stringify( messagePlaceholders ),
                      presentation,
                      referenceDirection,
                      referenceSetId,
                      schema,
                      user ? user.language : undefined,
                      JSON.stringify( value )
                  ]
              ),
              currentTransformedValue                                 = useRef<TransformedValue[]>( transformedValue ),
              heightStyle                                             = useMemo(
                  () => {
                      const retVal = getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout );

                      if ( presentation === 'grid' && !retVal.minHeight ) {
                          retVal.minHeight = '20rem';
                      }

                      return retVal;
                  },
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                                         = useMemo(
                  () => {
                      const item = global.data[ dataContext ]?.items?.[ rootItemId ],
                            mR   = item ? messageUtils.getMessageTemplateStringResolver( item, referenceSet ) : undefined;

                      return ( messageKey : string ) => messageKey === ''
                          ? undefined
                          : mR
                              ? mR( __( messageKey, messagePlaceholders ) )
                              : __( messageKey, messagePlaceholders );
                  },
                  [
                      dataContext,
                      JSON.stringify( messagePlaceholders ),
                      referenceSet,
                      rootItemId
                  ]
              ),
              resolvedPaceholder                                      = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      messageResolver,
                      JSON.stringify( placeholder ),
                      user ? user.language : undefined
                  ]
              ),
              resolvedNewStatusId                                     = useMemo(
                  () => {
                      return newStatusId ?? metaModel[ schema ].getStatus( 'active' )!.id;
                  },
                  [
                      newStatusId
                  ]
              ),
              resolvedDeleteStatusId                                  = useMemo(
                  () => {
                      return deleteStatusId ?? metaModel[ schema ].getStatus( 'deleted' )!.id;
                  },
                  [
                      deleteStatusId
                  ]
              ),
              processAutoHeight                                       = useCallback(
                  () => {
                      if ( presentation === 'grid' && height === 0 ) {
                          const dataTableWrapperElement = document.querySelector( `#${elementId}_dataTable .p-datatable-wrapper` ) as HTMLDivElement | undefined,
                                dataTableElement        = document.querySelector( `#${elementId}_dataTable .p-datatable-table` ) as HTMLTableElement | undefined;

                          if ( dataTableWrapperElement && dataTableElement?.offsetHeight ) {
                              dataTableWrapperElement.style.minHeight = `${dataTableElement.offsetHeight}px`;
                              dataTableWrapperElement.style.height = `${dataTableElement.offsetHeight}px`;
                          }
                      }
                  },
                  [
                      presentation,
                      height,
                      elementId
                  ]
              ),
              buildUpdateValue                                        = useCallback(
                  ( removedTransformedValues : TransformedValue[], newTransformedValues : TransformedValue[] ) : ReferenceValue[] => {
                      const retVal : ReferenceValue[] = [];

                      // handle removed
                      if ( value ) {
                          for ( const removedTransformedValue of removedTransformedValues ) {
                              const valueToUpdate = ( Array.isArray( value ) ? value : [ value ] ).find( v => v.id === removedTransformedValue.id );

                              if ( valueToUpdate ) {
                                  retVal.push( {
                                      ...valueToUpdate,
                                      statusId: resolvedDeleteStatusId
                                  } );
                              }
                          }
                      }

                      // handle new
                      for ( const newTransformedValue of newTransformedValues ) {
                          if ( newTransformedValue.item ) {
                              retVal.push( {
                                  id        : 0,
                                  referenceSetId,
                                  srcItemId : referenceDirection === 'SrcToDest' ? rootItemId : newTransformedValue.item.id,
                                  destItemId: referenceDirection === 'SrcToDest' ? newTransformedValue.item.id : rootItemId,
                                  statusId  : newTransformedValue.statusId,
                                  item      : newTransformedValue.item
                              } );
                          }
                      }

                      return retVal;
                  },
                  [
                      referenceDirection,
                      referenceSetId,
                      resolvedDeleteStatusId,
                      rootItemId,
                      schema,
                      value
                  ]
              ),
              onFileUploadBeforeUpload                                = useCallback(
                  () => {
                      setLoading( true );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onFileUploadUpload                                      = useCallback(
                  ( event : FileUploadUploadEvent ) => {
                      setLoading( false );

                      if ( fileItemTypeId ) {
                          try {
                              const response = JSON.parse( event.xhr.response ) as FileUploadResult;

                              if ( response.success ) {
                                  const newTransformedValues : TransformedValue[] = [];

                                  for ( const file of event.files ) {
                                      const fileId = `${file.name}_${file.size}`;

                                      if ( response.data?.[ fileId ] ) {
                                          const name                            = file.name.substring( 0, file.name.lastIndexOf( '.' ) ),
                                                extension                       = file.name.substring( file.name.lastIndexOf( '.' ) + 1 ),
                                                itemAttributes : ItemAttributes = {
                                                    itemTypeId: fileItemTypeId,
                                                    name,
                                                    statusId  : resolvedNewStatusId,
                                                    properties: []
                                                };

                                          if ( propertySetIds?.contentType && file.type ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.contentType,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : file.type
                                              } );
                                          }
                                          if ( propertySetIds?.extension && extension ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.extension,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : extension
                                              } );
                                          }
                                          if ( propertySetIds?.fileID ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.fileID,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : -1 * response.data[ fileId ]
                                              } );
                                          }
                                          if ( propertySetIds?.originalName ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.originalName,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : file.name
                                              } );
                                          }
                                          if ( propertySetIds?.size ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.size,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : file.size
                                              } );
                                          }
                                          if ( propertySetIds?.uploadDate ) {
                                              itemAttributes.properties!.push( {
                                                  propertySetId: propertySetIds.uploadDate,
                                                  statusId     : resolvedNewStatusId,
                                                  value        : new Date()
                                              } );
                                          }

                                          const item = new Item( itemAttributes, dataContext, schema );

                                          newTransformedValues.push( {
                                              id        : 0,
                                              statusId  : resolvedNewStatusId,
                                              name      : '',
                                              size      : 0,
                                              uploadDate: undefined,
                                              sortLabel : '',
                                              itemId    : item.id,
                                              item      : item
                                          } );
                                      }
                                      else if ( response.error?.[ fileId ] ) {
                                          const error = response.error?.[ fileId ];

                                          let detail : React.ReactNode;

                                          if ( error.type === 'stack' ) {
                                              detail = parse( StackError.fromJSON( error ).toHTML() );
                                          }
                                          else if ( error.type === 'localizable' ) {
                                              detail = parse( LocalizableError.fromJSON( error ).toHTML() );
                                          }
                                          else if ( error.position ) {
                                              detail = parse( Error.fromJSON( error ).toHTML() );
                                          }
                                          else {
                                              detail = String( error.message ?? error );
                                          }

                                          fileUploadRef.current?.messagesRef?.current?.show( {
                                              severity: 'error',
                                              summary : __( 'YN.base.form.fileField.upload.error', { fileName: file.name } ),
                                              detail,
                                              sticky  : true
                                          } );
                                      }
                                  }

                                  const updateValue = buildUpdateValue( [], newTransformedValues );

                                  if ( updateValue.length ) {
                                      onChange( updateValue, {} );
                                  }
                              }
                          }
                          catch ( err ) {
                              console.error( err );
                          }
                      }
                  },
                  [
                      buildUpdateValue,
                      dataContext,
                      fileItemTypeId,
                      onChange,
                      propertySetIds,
                      resolvedDeleteStatusId,
                      schema
                  ]
              ),
              onFileUploadRemove                                      = useCallback(
                  ( event : FileUploadRemoveEvent ) => {
                      const transformedValueForFile = currentTransformedValue.current.find( tV => tV.name === event.file.name );

                      if ( transformedValueForFile ) {
                          const updateValue = buildUpdateValue( [ transformedValueForFile ], [] );

                          if ( updateValue.length ) {
                              onChange( updateValue, event.originalEvent );
                          }
                      }
                  },
                  [
                      buildUpdateValue,
                      onChange
                  ]
              ),
              onMouseOver                                             = useCallback(
                  ( event : React.MouseEvent<HTMLDivElement> ) => {
                      document.getElementById( elementId )?.classList.add( 'p-focus' );

                      event.stopPropagation();
                      event.preventDefault();
                  },
                  [
                      elementId
                  ]
              ),
              onMouseLeave                                            = useCallback(
                  ( event : React.MouseEvent<HTMLDivElement> ) => {
                      document.getElementById( elementId )?.classList.remove( 'p-focus' );

                      event.stopPropagation();
                      event.preventDefault();
                  },
                  [
                      elementId
                  ]
              ),
              onDragOver                                              = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          if ( event.dataTransfer?.dropEffect ) {
                              event.dataTransfer.dropEffect = 'copy';
                          }

                          document.getElementById( elementId )?.classList.add( 'p-focus' );

                          event.stopPropagation();
                          event.preventDefault();
                      }
                  },
                  [
                      elementId
                  ]
              ),
              onDragLeave                                             = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          if ( event.dataTransfer?.dropEffect ) {
                              event.dataTransfer.dropEffect = 'none';
                          }

                          document.getElementById( elementId )?.classList.remove( 'p-focus' );
                      }
                  },
                  [
                      elementId,
                      disabled
                  ]
              ),
              onDrop                                                  = useCallback(
                  ( event : React.DragEvent<HTMLDivElement> ) => {
                      if ( !disabled ) {
                          event.stopPropagation();
                          event.preventDefault();

                          fileUploadRef.current?.onFileSelect( event as unknown as FileUploadSelectEvent );

                          document.getElementById( elementId )?.classList.remove( 'p-focus' );
                      }
                  },
                  [
                      elementId,
                      disabled
                  ]
              ),
              onGridUploadButtonClick                                 = useCallback(
                  () => {
                      fileUploadRef.current?.getInput()?.click();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onGridDownloadButtonClick                               = useCallback(
                  () => {
                      if ( gridSelection.length && gridSelection.find( tV => tV.id < 0 ) === undefined ) {
                          const itemKeys : string[] = [];

                          for ( const transformedValueForRow of gridSelection ) {
                              const item = global.data[ dataContext ].items[ transformedValueForRow.itemId ];

                              if ( item ) {
                                  itemKeys.push( item.key );
                              }
                          }

                          if ( itemKeys.length ) {
                              const link = document.createElement( 'a' );

                              if ( itemKeys.length === 1 ) {
                                  link.setAttribute( 'href', `${BASE_URL}/download/${itemKeys[ 0 ]}` );
                              }
                              else {
                                  link.setAttribute( 'href', `${BASE_URL}/api/file/bundleDownload?itemKeys=${itemKeys.join( ',' )}` );
                              }
                              link.setAttribute( 'target', '_blank' );
                              link.setAttribute( 'download', 'fallback' );
                              link.click();
                          }
                      }
                  },
                  [
                      dataContext,
                      gridSelection
                  ]
              ),
              onGridDeleteButtonClick                                 = useCallback(
                  ( originalEvent : any ) => {
                      if ( gridSelection.length ) {
                          for ( const transformedValueForRow of gridSelection ) {
                              const updateValue = buildUpdateValue( [ transformedValueForRow ], [] );

                              if ( updateValue.length ) {
                                  onChange( updateValue, originalEvent );
                              }
                          }
                      }
                  },
                  [
                      buildUpdateValue,
                      gridSelection,
                      onChange
                  ]
              ),
              onGridResolveRowClass                                   = useCallback(
                  ( data : DataTableRowData<RowData[]> ) => {
                      const retVal : any = {},
                            item         = global.data[ dataContext ]?.items[ data.itemId ];

                      if ( item?.id > 0 ) {
                          retVal[ 'yn-clickable' ] = true;
                      }

                      if ( preview === 'disabled' ) {
                          retVal[ 'yn-rowexpander-hidden' ] = true;
                      }

                      return retVal;
                  },
                  [
                      dataContext,
                      preview
                  ]
              ),
              onGridRowClick                                          = useCallback(
                  ( event : DataTableRowClickEvent ) => {
                      const item = global.data[ dataContext ]?.items[ event.data.itemId ];

                      if ( item?.id > 0 ) {
                          const link = document.createElement( 'a' );

                          link.setAttribute( 'href', `${BASE_URL}/download/${item.key}` );
                          link.setAttribute( 'target', '_blank' );
                          link.click();
                      }

                      return true;
                  },
                  [
                      dataContext
                  ]
              ),
              onGridSort                                              = useCallback(
                  ( event : DataTableSortEvent ) => {
                      let newGridSortOrder = 'fileName';

                      if ( event.sortField === 'sizeBody' ) {
                          newGridSortOrder = 'fileSize'
                      }
                      else if ( event.sortField === 'uploadDateBody' ) {
                          newGridSortOrder = 'uploadDate';
                      }

                      if ( event.sortOrder === 1 ) {
                          newGridSortOrder += 'Asc';
                      }
                      else {
                          newGridSortOrder += 'Desc';
                      }

                      dispatch( setElusiveData( elementId, {
                          gridSortOrder: newGridSortOrder as FileFieldListingOrder
                      } ) );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onGridRowToggle                                         = useCallback(
                  ( event : DataTableRowToggleEvent ) => {
                      dispatch( setElusiveData( elementId, {
                          gridExpandedRows: event.data
                      } ) );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              onGridSelectionChange                                   = useCallback(
                  ( event : DataTableSelectionMultipleChangeEvent<TransformedValue[]> ) => {
                      dispatch( setElusiveData( elementId, {
                          gridSelection: event.value
                      } ) );

                      event.originalEvent.stopPropagation();
                      event.originalEvent.preventDefault();
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              rowExpansionTemplate                                    = useCallback(
                  ( rowData : RowData ) => {
                      return rowData.preview;
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              columnsCache : ColumnsCache                             = useMemo(
                  () => {
                      if ( presentation === 'grid' ) {
                          const newColumnsCache : ColumnsCache = { ...currentColumnsCache.current },
                                columnDefaults                 = {
                                    filter         : false,
                                    reorderable    : false,
                                    resizeable     : false,
                                    sortable       : true,
                                    bodyClassName  : 'yn-nowrap-top-column yn-nopadding-column',
                                    headerClassName: 'yn-header-column'
                                },
                                existingCellIds : string[]     = [];

                          let changed : boolean             = false,
                              columnId : keyof ColumnsCache | undefined,
                              cacheParameters : CacheParameters | undefined,
                              cacheParameterDiff : string[] = [];

                          tableMinWidth.current = 0;

                          // Columns
                          columnId = 'delete';
                          cacheParameters = {
                              id      : `${columnId}_header`,
                              statusId: 0,
                              disabled,
                              language: user ? user.language : undefined
                          };
                          tableMinWidth.current += 2;
                          cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );
                          if ( cacheParameterDiff.length ) {
                              changed = true;

                              newColumnsCache[ columnId ] = {
                                  columnCache: {
                                      cacheParameters,
                                      column: <Column
                                          key="delete"
                                          selectionMode="multiple"

                                          {...columnDefaults}

                                          body={undefined}
                                          sortable={false}

                                          bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                                          bodyStyle={{
                                              textAlign: 'center'
                                          }}
                                          headerClassName="yn-header-action-column"
                                          style={{
                                              flex : 'none',
                                              width: '2rem'
                                          }}
                                          filterHeaderClassName="yn-header-action-column"
                                      />
                                  },
                                  cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                              };
                          }

                          columnId = 'expand';
                          if ( preview !== 'disabled' ) {
                              cacheParameters = {
                                  id      : `${columnId}_header`,
                                  statusId: 0
                              };
                              tableMinWidth.current += 2;
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );
                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  newColumnsCache[ columnId ] = {
                                      columnCache: {
                                          cacheParameters,
                                          column: <Column
                                              key="expand"

                                              {...columnDefaults}

                                              expander

                                              body={undefined}
                                              reorderable={false}
                                              resizeable={false}
                                              sortable={false}

                                              bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                                              bodyStyle={{
                                                  textAlign: 'center'
                                              }}
                                              headerClassName="yn-header-action-column"
                                              style={{
                                                  flex : 'none',
                                                  width: '2rem'
                                              }}
                                              filterHeaderClassName="yn-header-action-column"
                                          />
                                      },
                                      cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                                  };
                              }
                          }
                          else {
                              delete newColumnsCache[ columnId ];
                          }

                          columnId = 'name';
                          cacheParameters = {
                              id      : `${columnId}_header`,
                              statusId: 0,
                              language: user ? user.language : undefined
                          };
                          tableMinWidth.current += 5;
                          cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );
                          if ( cacheParameterDiff.length ) {
                              changed = true;

                              newColumnsCache[ columnId ] = {
                                  columnCache: {
                                      cacheParameters,
                                      column: <Column
                                          key="fileName"
                                          field="nameBody"
                                          header={__( 'YN.base.form.fileField.fileName' )}

                                          {...columnDefaults}

                                          style={{
                                              flex    : '1 1 5rem',
                                              minWidth: '5rem'
                                          }}
                                      />
                                  },
                                  cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                              };
                          }

                          columnId = 'size';
                          cacheParameters = {
                              id      : `${columnId}_header`,
                              statusId: 0,
                              language: user ? user.language : undefined
                          };
                          tableMinWidth.current += 8;
                          cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );
                          if ( cacheParameterDiff.length ) {
                              changed = true;

                              newColumnsCache[ columnId ] = {
                                  columnCache: {
                                      cacheParameters,
                                      column: <Column
                                          key="fileSize"
                                          field="sizeBody"
                                          header={__( 'YN.base.form.fileField.fileSize' )}

                                          {...columnDefaults}

                                          style={{
                                              flex : 'none',
                                              width: '8rem'
                                          }}
                                      />
                                  },
                                  cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                              };
                          }

                          columnId = 'uploadDate';
                          cacheParameters = {
                              id      : `${columnId}_header`,
                              statusId: 0,
                              language: user ? user.language : undefined
                          };
                          tableMinWidth.current += 12;
                          cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.columnCache.cacheParameters ?? {}, cacheParameters );
                          if ( cacheParameterDiff.length ) {
                              changed = true;

                              newColumnsCache[ columnId ] = {
                                  columnCache: {
                                      cacheParameters,
                                      column: <Column
                                          key="fileUploadDate"
                                          field="uploadDateBody"
                                          header={__( 'YN.base.form.fileField.fileUploadDate' )}

                                          {...columnDefaults}

                                          style={{
                                              flex : 'none',
                                              width: '12rem'
                                          }}
                                      />
                                  },
                                  cellCache  : newColumnsCache[ columnId ]?.cellCache ?? {}
                              };
                          }


                          // cells
                          for ( let i = 0; i < transformedValue.length; i++ ) {
                              const rowId = `row_${elementId}_${transformedValue[ i ].id}`;

                              let cellId : string | undefined,
                                  body : React.ReactNode | React.ReactNode[],
                                  filePreview : React.ReactNode,
                                  bodyClassNames : string[] | undefined,
                                  dirty : boolean | undefined;

                              columnId = 'name';
                              cellId = `${rowId}_${columnId}`;
                              existingCellIds.push( cellId );
                              dirty = false;
                              cacheParameters = {
                                  id         : cellId,
                                  statusId   : transformedValue[ i ].statusId,
                                  referenceId: transformedValue[ i ].id,
                                  itemId     : transformedValue[ i ].itemId,
                                  preview,
                                  dirty,
                                  value      : transformedValue[ i ].name,
                                  language   : user ? user.language : undefined
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  const name = statusColoringRenderer(
                                      transformedValue[ i ].name,
                                      transformedValue[ i ].statusId,
                                      schema,
                                      `item_${elementId}_${transformedValue[ i ].id}_name`,
                                      `item_${elementId}_${transformedValue[ i ].id}_name`
                                  );

                                  body = undefined;
                                  bodyClassNames = [ 'yn-cell-renderer-el' ];

                                  // dirty marking
                                  if ( dirty || transformedValue[ i ].id <= 0 ) {
                                      bodyClassNames.push( 'yn-dirty-border yn-no-right-border' );
                                  }

                                  filePreview = undefined;
                                  if ( preview !== 'disabled' ) {
                                      const item = global.data[ dataContext ]?.items[ transformedValue[ i ].itemId ];

                                      if ( item && propertyTypeIds?.contentType && propertyTypeIds?.fileID && propertyTypeIds?.originalName ) {
                                          const bodyPreviewId = `preview_${item.id}`,
                                                contentType   = item.properties.get( propertyTypeIds.contentType )?.value as string ?? '',
                                                fileName      = item.properties.get( propertyTypeIds.originalName )?.value as string ?? '',
                                                fileId        = Math.abs( item.properties.get( propertyTypeIds.fileID )?.value as number ?? 0 ),
                                                isImage       = /^image\//.test( contentType ),
                                                isPdf         = /^application\/pdf/.test( contentType );

                                          if ( isImage || isPdf ) {
                                              let src : string | undefined;

                                              if ( item.id < 0 ) {
                                                  src = `${BASE_URL}/api/file/tmp?fileId=${fileId}&contentType=${contentType}&fileName=${fileName}&inline`
                                              }
                                              else {
                                                  src = `${BASE_URL}/download/${item.key}?inline`;
                                              }

                                              if ( src ) {
                                                  if ( isImage ) {
                                                      filePreview = <img
                                                          id={bodyPreviewId}
                                                          src={src}
                                                          alt={__( 'YN.base.form.fileField.preview.imageAlt' )}
                                                          className="preview-img"
                                                          style={{ width: '100%' }}
                                                      />;
                                                  }
                                                  else {

                                                      // TODO: load pdf data, cache it (elusiveData), extract height and give data and height to <object>
                                                      //  const doLoad = async () => {
                                                      //      const response = await xhr.post( ...
                                                      //  pdfjsLib.getDocument( pdfData );
                                                      //  previewElement.data = URL.createObjectURL( `${pdfData}#view=FitH&toolbar=0&navpanes=0` )

                                                      filePreview = <object
                                                          id={bodyPreviewId}
                                                          data={`${src}#view=FitH&toolbar=0&navpanes=0`}
                                                          type="application/pdf"
                                                          className="preview-img"
                                                          style={{ width: '100%', height: 'auto' }}
                                                          onLoad={( e ) => {
                                                              const loadingTask = pdfjsLib.getDocument( { url: src, withCredentials: true } );

                                                              loadingTask.promise.then( ( pdf : {
                                                                  getPage : ( arg0 : number ) => any;
                                                              } ) => {
                                                                  pdf.getPage( 1 ).then( ( page : {
                                                                      getViewport : ( arg0 : { scale : number; } ) => any;
                                                                  } ) => {
                                                                      const scale          = 1,
                                                                            viewport       = page.getViewport( {
                                                                                scale: scale
                                                                            } ),
                                                                            previewElement = e.target as HTMLObjectElement;

                                                                      previewElement.style.height = `${Math.floor( ( viewport.height / viewport.width ) * previewElement.offsetWidth )}px`;
                                                                      processAutoHeight();
                                                                  } );
                                                              } );
                                                          }}
                                                      />;
                                                  }
                                              }
                                          }
                                      }
                                  }

                                  if ( !body ) {
                                      body = name;
                                  }

                                  body = <div className={bodyClassNames.join( ' ' )}>
                                      {body}
                                  </div>;

                                  newColumnsCache[ columnId ] = {
                                      columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                          cacheParameters,
                                          column: ''
                                      },
                                      cellCache  : {
                                          ...newColumnsCache[ columnId ]?.cellCache,
                                          [ cellId ]: {
                                              cacheParameters,
                                              body,
                                              preview: filePreview
                                          }
                                      }
                                  };
                              }

                              columnId = 'size';
                              cellId = `${rowId}_${columnId}`;
                              existingCellIds.push( cellId );
                              dirty = false;
                              cacheParameters = {
                                  id         : cellId,
                                  statusId   : transformedValue[ i ].statusId,
                                  referenceId: transformedValue[ i ].id,
                                  itemId     : transformedValue[ i ].itemId,
                                  value      : transformedValue[ i ].size,
                                  language   : user ? user.language : undefined
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  bodyClassNames = [ 'yn-cell-renderer-el' ];

                                  // dirty marking
                                  if ( dirty || transformedValue[ i ].id <= 0 ) {
                                      bodyClassNames.push( 'yn-dirty-border yn-no-left-border yn-no-right-border' );
                                  }

                                  body = statusColoringRenderer(
                                      formatBytes( transformedValue[ i ].size, 2, 1000 ),
                                      transformedValue[ i ].statusId,
                                      schema,
                                      `item_${elementId}_${transformedValue[ i ].id}_size`,
                                      `item_${elementId}_${transformedValue[ i ].id}_size`,
                                      bodyClassNames.join( ' ' )
                                  );

                                  newColumnsCache[ columnId ] = {
                                      columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                          cacheParameters,
                                          column: ''
                                      },
                                      cellCache  : {
                                          ...newColumnsCache[ columnId ]?.cellCache,
                                          [ cellId ]: {
                                              cacheParameters,
                                              body
                                          }
                                      }
                                  };
                              }

                              columnId = 'uploadDate';
                              cellId = `${rowId}_${columnId}`;
                              existingCellIds.push( cellId );
                              cacheParameters = {
                                  id         : cellId,
                                  statusId   : transformedValue[ i ].statusId,
                                  referenceId: transformedValue[ i ].id,
                                  itemId     : transformedValue[ i ].itemId,
                                  value      : transformedValue[ i ].uploadDate,
                                  language   : user ? user.language : undefined
                              };
                              cacheParameterDiff = objectUtils.getDiffKeys( newColumnsCache[ columnId ]?.cellCache?.[ cellId ]?.cacheParameters ?? {}, cacheParameters );

                              if ( cacheParameterDiff.length ) {
                                  changed = true;

                                  bodyClassNames = [ 'yn-cell-renderer-el' ];

                                  // dirty marking
                                  if ( dirty || transformedValue[ i ].id <= 0 ) {
                                      bodyClassNames.push( 'yn-dirty-border yn-no-left-border' );
                                  }

                                  body = statusColoringRenderer(
                                      transformedValue[ i ].uploadDate ? formatTimestamp( transformedValue[ i ].uploadDate! ) : '',
                                      transformedValue[ i ].statusId,
                                      schema,
                                      `item_${elementId}_${transformedValue[ i ].id}_uploadDate`,
                                      `item_${elementId}_${transformedValue[ i ].id}_uploadDate`,
                                      bodyClassNames.join( ' ' )
                                  );

                                  newColumnsCache[ columnId ] = {
                                      columnCache: newColumnsCache[ columnId ]?.columnCache ?? {
                                          cacheParameters,
                                          column: ''
                                      },
                                      cellCache  : {
                                          ...newColumnsCache[ columnId ]?.cellCache,
                                          [ cellId ]: {
                                              cacheParameters,
                                              body
                                          }
                                      }
                                  };
                              }
                          }

                          // cleanup not existingCellIds
                          for ( const columnCache of Object.values( newColumnsCache ) ) {
                              for ( const cellId of Object.keys( columnCache.cellCache ) as string[] ) {
                                  if ( !existingCellIds.includes( cellId ) ) {
                                      changed = true;
                                      delete columnCache.cellCache[ cellId ];
                                  }
                              }
                          }

                          if ( changed ) {
                              currentColumnsCache.current = newColumnsCache;
                          }
                      }

                      return currentColumnsCache.current;
                  },
                  [
                      disabled,
                      presentation,
                      preview,
                      schema,
                      transformedValue,
                      user ? user.language : undefined
                  ]
              ),
              gridColumns : React.ReactNode[]                         = useMemo(
                  () => {
                      const retVal : React.ReactNode[] = [];

                      if ( columnsCache.delete?.columnCache.column ) {
                          retVal.push( columnsCache.delete.columnCache.column );
                      }
                      if ( columnsCache.expand?.columnCache.column ) {
                          retVal.push( columnsCache.expand.columnCache.column );
                      }
                      if ( columnsCache.name?.columnCache.column ) {
                          retVal.push( columnsCache.name.columnCache.column );
                      }
                      if ( columnsCache.size?.columnCache.column ) {
                          retVal.push( columnsCache.size.columnCache.column );
                      }
                      if ( columnsCache.uploadDate?.columnCache.column ) {
                          retVal.push( columnsCache.uploadDate.columnCache.column );
                      }

                      return retVal;
                  },
                  [
                      columnsCache
                  ]
              ),
              rowData : RowData[]                                     = useMemo(
                  () => {
                      const newRowData : RowData[]  = [],
                            orderedTransformedValue = ( [] as TransformedValue[] ).concat( transformedValue );

                      let sortField : keyof TransformedValue = 'name';

                      if ( gridSortOrder.indexOf( 'fileSize' ) !== -1 ) {
                          sortField = 'size';
                      }
                      else if ( gridSortOrder.indexOf( 'uploadDate' ) !== -1 ) {
                          sortField = 'uploadDate';
                      }

                      orderedTransformedValue.sort( ( value1, value2 ) => {
                          const sortValue1 = typeof value1[ sortField ] === 'string'
                                    ? ( value1[ sortField ] as string ).toLowerCase()
                                    : value1[ sortField ],
                                sortValue2 = typeof value2[ sortField ] === 'string'
                                    ? ( value2[ sortField ] as string ).toLowerCase()
                                    : value2[ sortField ];

                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          if ( sortValue1 > sortValue2 ) {
                              return 1;
                          }
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          else if ( sortValue1 < sortValue2 ) {
                              return -1;
                          }
                          else {
                              return 0;
                          }
                      } );

                      if ( gridSortOrder.indexOf( 'Desc' ) !== -1 ) {
                          orderedTransformedValue.reverse();
                      }

                      for ( const transformedValueForRow of orderedTransformedValue ) {
                          const rowId = `row_${elementId}_${transformedValueForRow.id}`;

                          newRowData.push( {
                              ...transformedValueForRow,
                              deleteBody    : currentColumnsCache.current?.[ 'delete' ]?.cellCache[ `${rowId}_delete` ]?.body,
                              nameBody      : currentColumnsCache.current?.[ 'name' ]?.cellCache[ `${rowId}_name` ]?.body,
                              sizeBody      : currentColumnsCache.current?.[ 'size' ]?.cellCache[ `${rowId}_size` ]?.body,
                              uploadDateBody: currentColumnsCache.current?.[ 'uploadDate' ]?.cellCache[ `${rowId}_uploadDate` ]?.body,
                              preview       : currentColumnsCache.current?.[ 'name' ]?.cellCache[ `${rowId}_name` ]?.preview
                          } );
                      }

                      return newRowData;
                  },
                  [
                      columnsCache,
                      gridSortOrder,
                      transformedValue
                  ]
              ),
              fieldContent : JSX.Element | JSX.Element[ ] | undefined = useMemo(
                  () => {
                      let retValElements : JSX.Element | JSX.Element[] | undefined,
                          className = '';

                      if ( presentation === 'grid' ) {
                          const headerTemplate = ( options : FileUploadHeaderTemplateOptions ) => {
                                    const {
                                              chooseButton
                                          }                                                 = options,
                                          toolbarLayoutComponents : ToolbarLayoutComponents = {
                                              left : [],
                                              right: []
                                          },
                                          restrictions : ( string | JSX.Element )[]         = [];

                                    let expandedRows : any[] | DataTableExpandedRows | undefined = {};

                                    let sortField : string = 'nameBody';

                                    // upload button
                                    toolbarLayoutComponents.left.push( {
                                        type  : 'button',
                                        params: {
                                            key: `${uid}_uploadButton`,

                                            icon     : 'pi pi-upload',
                                            label    : __( 'YN.base.common.upload' ),
                                            onClick  : onGridUploadButtonClick,
                                            className: 'p-button-text',
                                            disabled
                                        }
                                    } );

                                    // download button
                                    toolbarLayoutComponents.left.push( {
                                        type  : 'button',
                                        params: {
                                            key: `${uid}_downloadButton`,

                                            icon     : 'pi pi-download',
                                            label    : __( 'YN.base.common.download' ),
                                            onClick  : onGridDownloadButtonClick,
                                            className: 'p-button-text',
                                            disabled : !gridSelection.length || gridSelection.find( tV => tV.id < 0 ) !== undefined
                                        }
                                    } );

                                    // delete button
                                    toolbarLayoutComponents.left.push( {
                                        type  : 'button',
                                        params: {
                                            key: `${uid}_deleteButton`,

                                            icon     : 'pi pi-trash yn-danger',
                                            label    : __( 'YN.base.common.delete' ),
                                            onClick  : onGridDeleteButtonClick,
                                            className: 'p-button-text',
                                            disabled : disabled || !gridSelection.length
                                        }
                                    } );

                                    // file size restriction
                                    if ( maxFileSize ) {
                                        if ( restrictions.length ) {
                                            restrictions.push( <br/> );
                                        }
                                        restrictions.push( __( 'YN.base.form.fileField.filesize.restriction', { fileSize: formatBytes( maxFileSize, 2, 1000 ) } ) );
                                    }

                                    // file type restriction
                                    if ( acceptedFiles ) {
                                        if ( restrictions.length ) {
                                            restrictions.push( <br/> );
                                        }
                                        restrictions.push( __( 'YN.base.form.fileField.acceptedFiles.restriction', { acceptedFiles } ) );
                                    }

                                    if ( restrictions.length ) {
                                        toolbarLayoutComponents.right.push( {
                                            type  : 'genericNode',
                                            params: <span className="yn-fileupload-restrictions">{restrictions}</span>
                                        } );
                                    }

                                    const toolbarComponents : ToolbarComponents = getToolbarComponents( toolbarLayoutComponents );

                                    if ( gridSortOrder.indexOf( 'fileSize' ) !== -1 ) {
                                        sortField = 'sizeBody';
                                    }
                                    else if ( gridSortOrder.indexOf( 'uploadDate' ) !== -1 ) {
                                        sortField = 'uploadDateBody';
                                    }

                                    // TODO: handle expanded rows here
                                    if ( !gridExpandedRows && preview === 'expanded' ) {
                                        for ( const rd of rowData ) {
                                            if ( rd.preview ) {
                                                expandedRows[ rd.id ] = true;
                                            }
                                        }
                                    }
                                    else {
                                        expandedRows = gridExpandedRows;
                                    }

                                    return <>
                                        <div className="hidden">
                                            {chooseButton}
                                        </div>
                                        <Toolbar
                                            id={`${uid}_toolbar`}
                                            left={toolbarComponents.left}
                                            right={toolbarComponents.right}
                                            className={`yn-panel-toolbar yn-toolbar-horizontal-separator-bottom`}
                                            style={{ zIndex: 5 }}
                                        />
                                        <DataTable
                                            key={`${elementId}_dataTable`}
                                            id={`${elementId}_dataTable`}

                                            value={rowData}
                                            dataKey="id"

                                            expandedRows={expandedRows}
                                            onRowToggle={onGridRowToggle}
                                            rowExpansionTemplate={rowExpansionTemplate}

                                            emptyMessage={<div className="flex align-items-center flex-column yn-filefield-empty"
                                                               style={{ width: '100%' }}>
                                                <i className="pi pi-file mt-3 p-5"
                                                   style={{ fontSize: '4rem', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}/>
                                                <span className="my-5" style={{ fontSize: '1.2rem', color: 'var(--text-color-secondary)' }}>
                                                    {__( 'YN.base.form.fileField.dragndrop.label' )}
                                                </span>
                                            </div>}

                                            selectionMode="checkbox"
                                            selection={gridSelection}
                                            onSelectionChange={onGridSelectionChange}

                                            onRowClick={onGridRowClick}

                                            rowClassName={onGridResolveRowClass}
                                            className={`p-datatable-striped yn-datatable-no-last-row-border`}
                                            style={{ width: '100%', zIndex: 1 }}
                                            tableStyle={{ minWidth: `${tableMinWidth.current}rem` }}

                                            columnResizeMode="expand"

                                            scrollable
                                            scrollHeight="flex"

                                            sortField={sortField}
                                            sortOrder={gridSortOrder.indexOf( 'Asc' ) !== -1 ? 1 : -1}
                                            onSort={onGridSort}

                                            lazy
                                            loading={loading}

                                            pt={{
                                                wrapper: {
                                                    style: {
                                                        '--table-width'        : '100%',
                                                        '--table-header-height': '2.2rem'
                                                    }
                                                }
                                            }}
                                        >
                                            {gridColumns}
                                        </DataTable>
                                    </>;
                                },
                                itemTemplate   = ( file : object, options : ItemTemplateOptions ) : React.ReactNode => {
                                    return undefined;
                                };

                          retValElements = <FileUpload
                              ref={fileUploadRef}
                              id={elementId}

                              name="data"
                              url={`${BASE_URL}/api/file/upload`}
                              auto={true}
                              multiple={true}
                              withCredentials={true}
                              accept={acceptedFiles ? acceptedFiles : undefined}
                              maxFileSize={maxFileSize ? maxFileSize : undefined}

                              onBeforeUpload={onFileUploadBeforeUpload}
                              onUpload={onFileUploadUpload}
                              onRemove={onFileUploadRemove}

                              headerTemplate={headerTemplate}
                              itemTemplate={itemTemplate}
                              emptyTemplate={undefined}

                              invalidFileSizeMessageSummary={__( 'YN.base.form.fileField.filesize.invalid.summary' )}
                              invalidFileSizeMessageDetail={__( 'YN.base.form.fileField.filesize.invalid.detail' )}
                              invalidFileAcceptMessageSummary={__( 'YN.base.form.fileField.accept.invalid.summary' )}
                              invalidFileAcceptMessageDetail={__( 'YN.base.form.fileField.accept.invalid.detail' )}

                              className={inputClassNames.join( ' ' )}
                              style={{ ...defaultProps.inputStyle, ...inputStyle }}
                          />;
                      }
                      else {
                          retValElements = <StaticContentField
                              {..._.pick( props, staticContentFieldComponentProps ) as StaticContentFieldComponentProps}

                              label={undefined}
                              labelVisibility="hidden"
                              infoTextLayout="text"
                              height={1}
                              content={`presentation '${presentation}' not implemented yet, please speak with your developer...`}
                              inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                              inputStyle={{
                                  border: 'none',
                                  ...getHeightStyle( {}, 1, 0, labelVisibility, labelPosition, infoTextLayout )
                              }}
                              messageClassNames={[ 'hidden' ]}
                              style={{ marginBottom: 0 }}
                          />;

                          className = 'yn-error-border';
                      }

                      return <FieldWrapper
                          {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

                          uid={elementId}

                          isDirty={markDirty && JSON.stringify( value ) !== JSON.stringify( originalValue )}

                          autoComplete={false}
                          pt={pt?.fieldWrapper}
                      >
                          <div id={elementId}
                               className={`yn-filefield yn-presentation-${presentation} ${className} ${disabled ? 'p-disabled' : ''}`}
                               style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }} onDragOver={onDragOver}
                               onDragLeave={onDragLeave} onDrop={onDrop} onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
                              {retValElements}
                          </div>
                      </FieldWrapper>;
                  },
                  [
                      disabled,
                      errorMessage, // check if errors really changed
                      gridColumns,
                      gridExpandedRows,
                      gridSelection,
                      JSON.stringify( heightStyle ),
                      JSON.stringify( inputClassNames ),
                      JSON.stringify( inputStyle ),
                      loading,
                      markDirty,
                      JSON.stringify( originalValue ), // check if originalValue really changed
                      presentation,
                      preview,
                      referenceSet,
                      resolvedPaceholder,
                      rowData,
                      transformedValue,
                      user ? user.language : undefined
                  ]
              );

        // use refs for dynamic resolving
        currentTransformedValue.current = transformedValue;
        currentReferenceSet.current = referenceSet;

        useLayoutEffect(
            () => {
                processAutoHeight();
            },
            [
                gridExpandedRows
            ]
        );

        // remove values from gridSelection not in transformedValue
        useEffect(
            () => {
                const newGridSelection = ( [] as TransformedValue[] ).concat( gridSelection );
                let changed = false;

                for ( let i = newGridSelection.length - 1; i >= 0; i-- ) {
                    if ( !transformedValue.find( tV => tV.id === newGridSelection[ i ].id ) ) {
                        changed = true;
                        newGridSelection.splice( i, 1 );
                    }
                }

                if ( changed ) {
                    dispatch( setElusiveData( elementId, {
                        gridSelection: newGridSelection
                    } ) );
                }
            },
            [
                gridSelection,
                transformedValue
            ]
        )

        return fieldContent;
    }
};

FileField.defaultProps = defaultProps;

export default FileField;
